import 'react-toastify/dist/ReactToastify.minimal.css';
import './App.scss';
import './styles/global/_autosuggest.scss';
import './styles/global/_base.scss';
import './styles/global/_forms.scss';
import './styles/global/_tag.scss';
import './styles/global/_tooltip.scss';
import './styles/global/_unhide.scss';
import './styles/global/_utils.scss';
import './styles/vendors/react-toastify/main.scss';

import { Suspense, useState } from 'react';
import { HelmetData } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import LoadingBar from 'react-top-loading-bar';
import FlowDebugOverlay from './components/debug/FlowDebugOverlay';
import SnackbarContainer from './components/elements/notifications/Snackbar/SnackbarContainer';
import AppPromotionCard, {
  checkIfDismissedInSession as appPromoCardDismissedInSession,
  pageHasOwnAppPrompt,
} from './components/elements/Card/AppPromotionCard';
import SistaminutenPromotionCard, {
  allowSistaminutenPromotionCard,
  checkIfDismissedInSession as sistaminutenPromoCardDismissedInSession,
} from './components/elements/Card/SistaminutenPromotionCard';
import './styles/tailwind.css';
import './styles/vendors/react-toastify/main.scss';

import NpsModal from '@/components/modules/modals/NpsModal';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { featurePromoActions } from './actions/featurePromoActions';
import useStartupEvents from './App.hooks';
import { isCriticalCss } from './AppSSR';
import FeaturePromo, {
  checkIfClosedPreviously,
  checkIfScreenBlocked,
  FeaturePromoProps,
} from './components/elements/FeaturePromo/FeaturePromo';
import LoadingPlaceHolder from './components/elements/LoadingPlaceholder';
import { AlertPortal } from './components/modules/alert/AlertPortal';
import AppRouter from './components/modules/app/AppRouter';
import AppRoutes from './components/modules/app/AppRoutes';
import { AllowCookies } from './components/modules/modals';
import AppPromotionModal from './components/modules/modals/AppPromotionModal';
import LastMinuteModal from './components/modules/modals/LastMinuteModal';
import LoginModal from './components/modules/modals/LoginModal';
import UserProfileOnboarding from './components/modules/modals/UserProfileOnboarding';
import featurePromoConfig from './config/feature-promos.json';
import COOKIES from './constants/cookies';
import { WEB_TEST_FLAG } from './constants/experimentConstants';
import AssociationDrawer from './features/associations-drawer/AssociationDrawer';
import { useAssocationDrawerContext } from './features/associations-drawer/AssociationDrawer.hooks';
import UserInfoBanner from './features/user-info-banner/UserInfoBanner';
import { getContainmentPolicy } from './helpers/containmentPolicy';
import { getCookie, isServer, isSistaminuten, renameCookie } from './helpers';
import { interruptionsAllowed, profileOnboardingAllowed } from './helpers/embedPolicy';
import { useAppSelector } from './hooks';
import { useConfidenceFlag } from './hooks/useConfidenceFlag';
import useMobileView from './hooks/useMobileView';
import useSaveGiftcardFromLink from './hooks/useSaveGiftcardFromLink';

export type AppProps = {
  bootstrapStyles?: string[];
  preload?: any[];
  helmet?: HelmetData;
  isMobile?: boolean;
  location?: any;
  context: any;
};

const App = ({ isMobile = false, bootstrapStyles = [] }: AppProps) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [visible, setVisible] = useState(true);
  const [featurePromos] = useState(featurePromoConfig);
  const featurePromoTrigger = useAppSelector((state) => state.featurePromos)?.featurePromoTrigger;
  const loading = useAppSelector((state) => state.loading);
  const { nps, redirect, login, userProfileOnboarding, lastMinute } = useAppSelector((state) => state.modal);
  const { user } = useAppSelector((state) => state.users);
  const { isMobileView } = useMobileView();
  // Two independent reasons to hold interruptions back: the embed runtime (framed or
  // in one of the widget popups) and a contained flow, whatever surface contains it.
  const canInterrupt = interruptionsAllowed() && getContainmentPolicy().allowsInterruptions;
  useConfidenceFlag(WEB_TEST_FLAG.name, WEB_TEST_FLAG.defaultValue);

  // effects
  useStartupEvents(
    isMobile,
    bootstrapStyles.filter((style) => !isCriticalCss(style)),
    setVisible,
  );
  useSaveGiftcardFromLink();

  // computed
  const loadingbarProps = {
    color: isSistaminuten() ? '#ffcb2e' : '#23A994',
    progress: !loading.is404 && loading.show ? 50 : 100,
  };
  const cookieAcceptedOldName = Boolean(getCookie('CookiePreferencesMP'));
  const cookieAccepted = Boolean(getCookie(COOKIES.BOKADIREKT_COOKIE_PREFERENCES_MP));
  const hasSavedGiftcard = !isServer ? sessionStorage.getItem('savedGiftcard') === 'true' : undefined;
  const hideCookiesModal = cookieAccepted || cookieAcceptedOldName;

  const showLoginModal = login?.show;
  const showCompleteProfileModal = !showLoginModal && userProfileOnboarding?.show && profileOnboardingAllowed();
  // Framed or otherwise contained, suppress non-booking interruptions (promos, NPS, app install, last-minute).
  const showLastMinuteModal =
    canInterrupt && lastMinute?.show && hideCookiesModal && !showLoginModal && !showCompleteProfileModal;
  const showNpsModal =
    canInterrupt &&
    nps?.show &&
    hideCookiesModal &&
    !showLoginModal &&
    !showCompleteProfileModal &&
    !showLastMinuteModal;
  const showAppQrCode =
    canInterrupt && !showLoginModal && !showCompleteProfileModal && hideCookiesModal && hasSavedGiftcard && !isMobile;
  const showFeaturePromos = false && !showLastMinuteModal && featurePromos.length > 0;

  const showSistaminutenPromotionCard =
    canInterrupt &&
    !nps?.show &&
    !showLoginModal &&
    !showCompleteProfileModal &&
    hideCookiesModal &&
    !showFeaturePromos &&
    !appPromoCardDismissedInSession() &&
    !pageHasOwnAppPrompt(location.pathname, Boolean(user?.token)) &&
    allowSistaminutenPromotionCard(location.pathname);

  const showAppPromotionCard =
    canInterrupt &&
    !nps?.show &&
    !showLoginModal &&
    !showCompleteProfileModal &&
    hideCookiesModal &&
    !showFeaturePromos &&
    !sistaminutenPromoCardDismissedInSession() &&
    !showSistaminutenPromotionCard;

  if (cookieAcceptedOldName) renameCookie('CookiePreferencesMP', COOKIES.BOKADIREKT_COOKIE_PREFERENCES_MP);

  const triggerFeaturePromotion = () =>
    dispatch(featurePromoActions.updateFeaturePromoTrigger({ id: 'sistaminuten_v2' }));

  useEffect(() => {
    if (hideCookiesModal) {
      setTimeout(() => {
        triggerFeaturePromotion();
      }, 500);
    }
  }, []);

  return (
    <div className="App" style={{ visibility: visible ? 'visible' : 'hidden' }}>
      <UserInfoBanner />

      {!loading.loadingBarHidden && <LoadingBar transitionTime={300} {...loadingbarProps} />}

      {/* Modals */}
      {!hideCookiesModal && <AllowCookies onAllowCallback={triggerFeaturePromotion} />}
      {showSistaminutenPromotionCard && <SistaminutenPromotionCard />}
      {showAppPromotionCard && <AppPromotionCard />}
      {showLoginModal && <LoginModal isOpen />}
      {showCompleteProfileModal && <UserProfileOnboarding isOpen />}
      {showNpsModal && <NpsModal isOpen />}
      {showLastMinuteModal && <LastMinuteModal isOpen />}
      {showAppQrCode && <AppPromotionModal />}

      {showFeaturePromos &&
        featurePromos.map((promo) => {
          if (
            isServer ||
            !promo.id ||
            checkIfClosedPreviously(promo.id) ||
            checkIfScreenBlocked(promo?.blockScreens ?? [])
          ) {
            return null;
          }

          const config = isMobileView ? promo?.['mobile'] : promo?.['desktop'];
          const start = Boolean(featurePromoTrigger[promo?.id]?.triggered);

          const props: FeaturePromoProps = {
            key: promo?.id,
            id: promo.id,
            start,
            ...config,
          };

          return <FeaturePromo {...props} />;
        })}

      {/* Router */}
      <Suspense fallback={<LoadingPlaceHolder className="fixed h-screen" />}>
        <AppRoutes redirect={redirect} />
      </Suspense>

      {/* Portals */}
      <AssociationDrawer {...useAssocationDrawerContext()} />
      <AlertPortal />
      <SnackbarContainer />

      {/* Debug Overlay */}
      <FlowDebugOverlay />
    </div>
  );
};

export default function AppWrapper(props: AppProps) {
  const { location, context } = props;

  return (
    <AppRouter {...{ location, context }}>
      <App {...props} />
    </AppRouter>
  );
}
